// Bileli · app/store — the web-app ROUTE: which page/context fills the BileliWorkspace overlay. why: the
// ONE bridge from clean Bileli code into Telegram's global state — Views/ViewModels call these helpers and
// NEVER import Telegram's global directly. `home` kinds (dashboard/catalog/…) are the top-level nav pages;
// course/editor/lesson are drill-ins. Transient (not persisted). Reuses global.bileliContext (no new field).
//
// NAV-STACK: every openX()/navigate() call now ALSO records a session nav-stack entry (navStack.ts) that
// BileliHistoryHandler projects onto useHistoryBack records — so browser Back walks the real visit order
// (tabs, mode switches, drill-ins) instead of exiting to chat. The stack is per-tab module state:
// deliberately NOT in Telegram's multitab-shared `global` (window.history is per-tab) and not persisted
// (reload seeds a parent chain from the URL via seedFromRoute). Public openX() signatures are unchanged.

import { getActions, getGlobal, setGlobal } from '../../../global';

import type { GlobalState } from '../../../global/types';
import type { BileliActiveMode } from '../../../util/bileli/types';
import type { NavEntry } from '../router/navStack';
import type { BileliRouteMode } from '../router/routes';
import type { BileliContext, BileliHomeKind } from './bileliContext.types';
import { LeftColumnContent } from '../../../types';

import { selectSharedSettings } from '../../../global/selectors/sharedState';
import {
  popBelow, push, replaceTop, seedChain, TAIL_ON_CHAT, truncateTail, withScrollTop,
} from '../router/navStack';
import {
  buildHash, homeContextOf, modeOfKind, parentOf,
} from '../router/routes';

// Bileli: the kind/type vocabulary lives in bileliContext.types.ts (pure — the router + its unit tests
// import it without this file's Telegram-global dependency); re-exported here so consumers are unchanged.
export { BILELI_HOME_KINDS, isHomeKind } from './bileliContext.types';
export type { BileliContext, BileliContextKind, BileliHomeKind } from './bileliContext.types';

export function selectBileliContext(global: GlobalState): BileliContext | undefined {
  return global.bileliContext;
}

function setBileliContext(context: BileliContext | undefined): void {
  // tt-multitab safety: setGlobal must receive the `global` variable it was derived from.
  let global = getGlobal();
  global = { ...global, bileliContext: context };
  setGlobal(global);
}

// ---------------------------------------------------------------------------------------------------
// Nav-stack state (per-tab module state; see header note).

let navEntries: readonly NavEntry[] = [];
let navSeq = 0;
let scrollTopGetter: (() => number | undefined) | undefined;

function currentRouteMode(): BileliRouteMode {
  const { activeMode } = selectSharedSettings(getGlobal());
  return activeMode === 'teach' ? 'teach' : 'learn';
}

/** Mode of an entry: pinned kinds follow KIND_MODE; bi-modal kinds (lesson/profile/provider) follow
 * the active mode — exactly the hash behavior the old handler had. */
function resolveMode(context: BileliContext): BileliRouteMode {
  return modeOfKind(context.kind) ?? currentRouteMode();
}

function makeEntry(context: BileliContext, mode: BileliRouteMode): NavEntry {
  return {
    seq: ++navSeq,
    mode,
    context,
    hash: buildHash(mode, context) ?? (mode === 'teach' ? 'teach' : 'learn'),
  };
}

function topEntry(): NavEntry | undefined {
  return navEntries[navEntries.length - 1];
}

function commitPush(context: BileliContext): void {
  const mode = resolveMode(context);
  const entry = makeEntry(context, mode);
  const top = topEntry();
  if (top && top.hash === entry.hash) {
    // Same URL → no new history entry, but REFRESH the context: ephemeral fields not encoded in the
    // hash (startTimeSec rewatch seeks) must still reach the screen.
    navEntries = replaceTop(navEntries, { ...top, context });
  } else {
    navEntries = push(withScrollTop(navEntries, scrollTopGetter?.()), entry);
  }
  setBileliContext(context);
}

function commitReplaceTop(context: BileliContext): void {
  const mode = resolveMode(context);
  navEntries = replaceTop(navEntries, makeEntry(context, mode));
  setBileliContext(context);
}

/** Apply a (new) top entry to the app: context + mode (cross-mode Back restores Учёба⇄Препод). */
function applyEntry(entry: NavEntry): void {
  setBileliContext(entry.context);
  const { activeMode } = selectSharedSettings(getGlobal());
  if (entry.mode !== activeMode) {
    getActions().setSharedSettingOption({ activeMode: entry.mode });
  }
}

/** The workspace registers how to read the page's scrollTop (captured when a push covers an entry). */
export function registerScrollTopGetter(getter: (() => number | undefined) | undefined): void {
  scrollTopGetter = getter;
}

export function getNavEntries(): readonly NavEntry[] {
  return navEntries;
}

export function getTopNavEntry(): NavEntry | undefined {
  return topEntry();
}

/** In-app back buttons (workspace ←): follow the REAL visit order, same as browser Back. */
export function goBack(): void {
  const top = topEntry();
  if (!top || navEntries.length <= 1) return; // at the root — in-app back has nowhere to go
  navEntries = popBelow(navEntries, top.seq);
  const next = topEntry();
  if (next) applyEntry(next);
}

/** Browser-Back plumbing (BileliHistoryHandler): pop everything from `seq` up; returns the new top —
 * undefined means the stack emptied (the handler exits to chat). Idempotent per navStack.popBelow. */
export function popBelowSeq(seq: number): NavEntry | undefined {
  navEntries = popBelow(navEntries, seq);
  const next = topEntry();
  setBileliContext(next?.context);
  return next;
}

/** Rail mode select: PUSH the mode's home (browser Back then crosses the seam back). */
export function pushModeHome(mode: BileliRouteMode): void {
  commitPush(homeContextOf(mode));
}

/** Rail chat select: keep a short trail (the stack survives the detour), hide the workspace. */
export function truncateForChat(): void {
  navEntries = truncateTail(navEntries, TAIL_ON_CHAT);
  setBileliContext(undefined);
}

/**
 * THE mode-switch seam — single-sourced here so the rail (chat mode) and the AppHeader segmented
 * control (Bileli modes, chrome-B desktop) can't drift. Chat: show the chat list + keep a short nav
 * trail. Learn/Teach: drop any open chat (its #<chatId> would fight the Bileli hash) + PUSH the mode
 * home so browser Back crosses the seam like any SPA top-nav.
 */
export function switchMode(mode: BileliActiveMode): void {
  const actions = getActions();
  actions.setSharedSettingOption({ activeMode: mode });
  if (mode === 'chat') {
    actions.openLeftColumnContent({ contentKey: LeftColumnContent.ChatList });
    truncateForChat();
  } else {
    actions.openChat({ id: undefined });
    pushModeHome(mode);
  }
}

/** Reload/deep-link restore: seed the whole ancestor chain from the route table (one source of truth). */
export function seedFromRoute(context: BileliContext, mode: BileliRouteMode): void {
  navEntries = seedChain(
    context,
    mode,
    parentOf,
    (ctx, m) => makeEntry(ctx, modeOfKind(ctx.kind) ?? m),
  );
  setBileliContext(context);
}

// ---------------------------------------------------------------------------------------------------
// Navigation verbs (public signatures unchanged — history intent now lives here, not in the handler).

/** Navigate to a top-level home page (no course selected) — AppHeader nav tabs. PUSHES history. */
export function navigate(kind: BileliHomeKind): void {
  commitPush({ kind });
}

/** Open a course's public landing/detail (learn). */
export function openCourse(courseId: string): void {
  commitPush({ kind: 'course', courseId });
}

/** Open the teacher course editor (teach). */
export function openCourseEditor(courseId: string): void {
  commitPush({ kind: 'editor', courseId });
}

/** Open a lesson (player for students / editor for teachers), optionally seeking the video to a second. */
export function openLesson(courseId: string, lessonId: string, startTimeSec?: number): void {
  commitPush({ kind: 'lesson', courseId, lessonId, startTimeSec });
}

/** True when the top entry is the SAME module workspace (any leaf) — leaf swaps replace, not push. */
function topIsLeafOfModule(courseId: string, moduleId: string): boolean {
  const top = topEntry()?.context;
  return top?.kind === 'module' && top.courseId === courseId && top.moduleId === moduleId
    && Boolean(top.lessonId || top.isDraft);
}

/** Open the teacher MODULE WORKSPACE (2-pane lesson editor), optionally on a specific lesson. */
export function openModule(courseId: string, moduleId: string, lessonId?: string): void {
  const context: BileliContext = lessonId
    ? { kind: 'module', courseId, moduleId, lessonId }
    : { kind: 'module', courseId, moduleId };
  // draft → lesson (or lesson → lesson) inside one module: URL REPLACE — Back must not revisit the
  // stale leaf (the old hash-keyed remount had exactly these semantics).
  if (lessonId && topIsLeafOfModule(courseId, moduleId)) {
    commitReplaceTop(context);
  } else {
    commitPush(context);
  }
}

/** Open the module workspace on the "add lesson" (draft) screen. */
export function openModuleDraft(courseId: string, moduleId: string): void {
  const context: BileliContext = {
    kind: 'module', courseId, moduleId, isDraft: true,
  };
  if (topIsLeafOfModule(courseId, moduleId)) {
    commitReplaceTop(context);
  } else {
    commitPush(context);
  }
}

/** Open a course's student roster (teach analytics — the cohort is picked in-page). */
export function openRoster(courseId: string): void {
  commitPush({ kind: 'roster', courseId });
}

/** Open one student's 360° progress page (teach analytics). */
export function openStudent(cohortId: string, studentId: string): void {
  commitPush({ kind: 'student', cohortId, studentId });
}

/** Open a course's live-sessions (эфиры) page (teach). */
export function openLiveSessions(courseId: string): void {
  commitPush({ kind: 'live', courseId });
}

/** Open a program's public landing (learn) — banner tap / program links. */
export function openProgram(programSlug: string): void {
  commitPush({ kind: 'program', programSlug });
}

/** Open a provider (org) public profile — BI-MODAL like `profile` (viewable from teach too). */
export function openProvider(orgSlug: string): void {
  commitPush({ kind: 'provider', orgSlug });
}

/** Open the teacher program editor (teach). */
export function openProgramEditor(programSlug: string): void {
  commitPush({ kind: 'program-editor', programSlug });
}

/** Full exit: clear the selection AND the nav trail → the surface falls back to its mode's home. */
export function closeBileliContext(): void {
  navEntries = [];
  setBileliContext(undefined);
}
